import {
  Button,
  Card,
  Checkbox,
  Divider,
  Form,
  FormProps,
  Input,
  message,
  Modal,
  Popconfirm,
  Select,
  Space,
  Upload,
  UploadFile,
} from "antd";
import {
  CheckCircleOutlined,
  DeleteOutlined,
  FilePdfOutlined,
  SaveOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Typography } from "../../material-old";
import React, { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks.ts";
import {
  changeStatusRequirementByID,
  fetchRequirementById,
  onRemoveRequirement,
  verifyRequirement,
} from "../../../redux/actions/requirementsActions.tsx";
import {
  fetchFolderById,
  setActiveFolderAsync,
} from "../../../redux/actions/folderActions.tsx";
import { availableStatusOptions } from "../../folders/fixtures/availableStatusOptions.tsx";
import { FormView } from "../../folders/FormView.tsx";
import { Files } from "./Files.tsx";
import { clearActiveRequirement } from "../../../redux/slices/requirementsSlice.ts";
import { parseDateToSpanishString } from "../../../utils/common/parseDateToSpanishString.ts";
import { REJECTION_REASONS } from "../../../constants/rejectionReasons.ts";
import { VERIFIABLE_SUBFOLDER_SLUGS } from "../../../constants/verifiableRequirements.ts";
import { extractErrorMessage } from "../../../utils/extractErrorMessage.ts";
import { useUploadIdFiles } from "../../../hooks/vault/mutations/useUploadIdFiles.ts";
import { getDocumentUploadPolicy } from "../../../constants/uploadPolicies.ts";
import { isBdcAuthorizationRequirement } from "../../vault/utils/requirementGrouping.ts";
import api from "../../../api";

type StatusForm = {
  status: string;
  invalid_reason?: string;
  rejection_reason?: string;
};

export const DetailedView: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
  const [fileForm] = Form.useForm();
  const [statusForm] = Form.useForm();

  const dispatch = useAppDispatch();

  const { activeFolder, loading: loadingFolder } = useAppSelector(
    (state) => state.folder,
  );
  const { activeRequirement, loading } = useAppSelector(
    (state) => state.requirements,
  );
  const uploadIdFilesMutation = useUploadIdFiles(activeFolder?.id);

  const [messageApi, contextHolder] = message.useMessage();
  const [isValidRequirement, setIsValidRequirement] = useState<boolean>(true);
  const [deleteRequirementLoading, setDeleteRequirementLoading] =
    useState<boolean>(false);
  const [selectedRejectionReason, setSelectedRejectionReason] =
    useState<string>("");
  const [verifyLoading, setVerifyLoading] = useState<boolean>(false);
  const [verificationData, setVerificationData] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [uploadUiLocked, setUploadUiLocked] = useState(false);
  const uploadInFlightRef = useRef(false);

  const files = activeRequirement?.files || [];
  const currentFiles = files.filter((file) => !file.is_verification_reference);
  const description = activeRequirement?.name;
  const updatedAt = activeRequirement?.updated_at;
  const documentType = activeRequirement?.form_values?.find(
    (formValue) => formValue.key === "document_type",
  )?.value;
  const uploadPolicy = getDocumentUploadPolicy({
    requirementSlug: activeRequirement?.slug,
    documentType: typeof documentType === "string" ? documentType : undefined,
    requirementStatus: activeRequirement?.status,
  });
  const minimumFiles = uploadPolicy.min_files;
  const maximumFiles = uploadPolicy.max_files;
  const appendsBankStatementCollection = Boolean(
    (activeRequirement?.status === "pending_upload" ||
      activeRequirement?.status === "pending_review") &&
      activeRequirement.slug === "proof-of-income" &&
      documentType === "bank_statement" &&
      currentFiles.length > 0,
  );
  const missingCollectionFiles = appendsBankStatementCollection
    ? Math.max(minimumFiles - currentFiles.length, 0)
    : minimumFiles;
  const maximumNewFiles = Math.max(
    maximumFiles - (appendsBankStatementCollection ? currentFiles.length : 0),
    0,
  );
  const collectionAtCapacity =
    appendsBankStatementCollection && maximumNewFiles === 0;
  const minimumNewFiles = collectionAtCapacity
    ? 0
    : appendsBankStatementCollection
      ? Math.max(missingCollectionFiles, 1)
      : minimumFiles;
  const isUploading = uploadIdFilesMutation.isPending;
  const mutationsLocked = isUploading || uploadUiLocked;
  const isNativeBdc =
    isBdcAuthorizationRequirement(
      activeRequirement?.slug ?? "",
      activeRequirement?.type ?? "",
    ) && activeRequirement?.is_native_bdc_run !== false;
  const hasValidFileCount =
    fileList.length >= minimumNewFiles && fileList.length <= maximumNewFiles;

  const isVerifiable = activeRequirement?.slug
    ? VERIFIABLE_SUBFOLDER_SLUGS.includes(activeRequirement.slug)
    : false;

  const handleRemoveRequirement = async () => {
    if (uploadInFlightRef.current || mutationsLocked) return;
    try {
      setDeleteRequirementLoading(true);

      await dispatch(onRemoveRequirement(activeRequirement!.id)).unwrap();
      await dispatch(fetchFolderById(activeFolder!.id)).unwrap();

      messageApi.open({
        type: "success",
        content: `Requisito ${activeRequirement?.name} eliminado.`,
      });

      setTimeout(() => dispatch(clearActiveRequirement()), 1000);
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo eliminar el requisito."),
      );
    } finally {
      setDeleteRequirementLoading(false);
    }
  };

  const handleOpenModal = () => {
    if (uploadInFlightRef.current || mutationsLocked) return;
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleVerifyRequirement = async () => {
    if (isNativeBdc || uploadInFlightRef.current || mutationsLocked) return;
    try {
      setVerifyLoading(true);
      setVerificationData(null);

      const response = await dispatch(
        verifyRequirement({
          requirement_id: activeRequirement!.id,
          file_ids: currentFiles.map((file) => file.id),
        }),
      ).unwrap();

      if (response?.processing) {
        setVerificationData(null);
        messageApi.open({
          type: "info",
          content: "El documento grande se está procesando.",
        });
      } else {
        setVerificationData(response);
        messageApi.open({
          type: "success",
          content: "Requisito verificado exitosamente.",
        });
      }

      await dispatch(fetchRequirementById(activeRequirement!.id)).unwrap();

      handleCloseModal();
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo verificar el requisito."),
      );
    } finally {
      setVerifyLoading(false);
    }
  };

  useEffect(() => {
    setFileList([]);
    statusForm.setFieldsValue({ status: activeRequirement?.status });
    const formReason = activeRequirement?.invalid_reason ?? null;
    statusForm.setFieldsValue({ invalid_reason: formReason });
    setIsValidRequirement(activeRequirement?.status !== "invalid");

    if (formReason) {
      const matchingReason = REJECTION_REASONS.find(
        (reason) => reason.label === formReason,
      );
      if (matchingReason) {
        setSelectedRejectionReason(matchingReason.value);
        statusForm.setFieldsValue({ rejection_reason: matchingReason.value });
      } else {
        setSelectedRejectionReason("other");
        statusForm.setFieldsValue({ rejection_reason: "other" });
      }
    } else {
      setSelectedRejectionReason("");
      statusForm.setFieldsValue({ rejection_reason: undefined });
    }

    setVerificationData(activeRequirement?.verification_data || null);
    setIsModalOpen(false);
  }, [activeRequirement, statusForm]);

  const onSubmitStatusForm: FormProps<StatusForm>["onFinish"] = async (
    data,
  ) => {
    if (uploadInFlightRef.current || mutationsLocked) return;
    const currentActiveMF = activeFolder;

    let invalidReason = data.invalid_reason;
    if (
      data.status === "invalid" &&
      data.rejection_reason &&
      data.rejection_reason !== "other"
    ) {
      const selectedReasonObj = REJECTION_REASONS.find(
        (reason) => reason.value === data.rejection_reason,
      );
      invalidReason = selectedReasonObj?.label || data.invalid_reason;
    }

    try {
      await dispatch(
        changeStatusRequirementByID({
          requirement_id: activeRequirement!.id,
          status: data.status,
          invalid_reason: invalidReason,
        }),
      ).unwrap();

      const activeFolder = await dispatch(
        fetchFolderById(currentActiveMF!.id),
      ).unwrap();
      await dispatch(setActiveFolderAsync(activeFolder)).unwrap();

      messageApi.open({
        type: "success",
        content: "Estado actualizado.",
      });

      setTimeout(() => dispatch(clearActiveRequirement()), 1000);
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo actualizar el requisito."),
      );
    }
  };

  const onUploadFileForm = async (_: any) => {
    if (uploadInFlightRef.current || mutationsLocked) return;
    uploadInFlightRef.current = true;
    setUploadUiLocked(true);
    try {
      const selectedFiles = fileList.flatMap((file) =>
        file.originFileObj ? [file.originFileObj] : [],
      );
      if (
        selectedFiles.length < minimumNewFiles ||
        selectedFiles.length > maximumNewFiles
      ) {
        throw new Error(
          minimumNewFiles === maximumNewFiles
            ? `Selecciona ${minimumNewFiles} archivo${minimumNewFiles === 1 ? "" : "s"}.`
            : `Selecciona entre ${minimumNewFiles} y ${maximumNewFiles} archivos.`,
        );
      }

      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append("files", file));
      if (typeof documentType === "string" && documentType) {
        formData.append("document_type", documentType);
      }

      if (isNativeBdc) {
        formData.set("upload_batch_action", "replace_evidence");
        await api.post(
          `/docvault/requirements/${activeRequirement!.id}/document-file/upload/`,
          formData,
        );
      } else {
        await uploadIdFilesMutation.mutateAsync({
          reqId: activeRequirement!.id,
          formData,
          uploadPolicy: {
            ...uploadPolicy,
            min_files: minimumNewFiles,
          },
          newStatus: appendsBankStatementCollection
            ? "pending_review"
            : activeRequirement!.status,
          uploadBatchAction: appendsBankStatementCollection
            ? undefined
            : "replace_evidence",
        });
      }

      fileForm.resetFields();
      setFileList([]);

      messageApi.open({
        type: "success",
        content: "Subida exitosa.",
      });

      try {
        await Promise.all([
          dispatch(fetchRequirementById(activeRequirement!.id)).unwrap(),
          dispatch(fetchFolderById(activeFolder!.id)).unwrap(),
        ]);
      } catch {
        messageApi.warning(
          "El archivo se subió, pero no se pudo actualizar la vista. Recarga la página.",
        );
      }
    } catch (error: any) {
      messageApi.error(
        extractErrorMessage(error, "No se pudo subir el archivo."),
      );
    } finally {
      uploadInFlightRef.current = false;
      setUploadUiLocked(false);
    }
  };

  const onChangeFileList = ({ fileList: newFileList }: any) => {
    setFileList(newFileList);
  };

  return (
    <Card title={activeRequirement?.name} bordered={false} className="w-full">
      {contextHolder}
      <div>
        <Form form={statusForm} onFinish={onSubmitStatusForm}>
          <Typography variant="h6" className="text-left mb-3">
            Acciones
          </Typography>
          <Space className="w-full flex flex-wrap">
            <Form.Item<StatusForm>
              name="status"
              className=""
              style={{ marginBottom: "0px" }}
            >
              <Select
                options={availableStatusOptions}
                style={{ width: 180 }}
                disabled={mutationsLocked}
                onChange={(value) => setIsValidRequirement(value !== "invalid")}
              />
            </Form.Item>

            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loadingFolder}
              disabled={mutationsLocked}
            >
              Guardar
            </Button>

            {isVerifiable && (
              <>
                <Button
                  color="orange"
                  variant="outlined"
                  icon={<CheckCircleOutlined />}
                  onClick={handleOpenModal}
                  disabled={currentFiles.length === 0 || mutationsLocked}
                >
                  {verifyLoading ? "Verificando..." : "Verificar"}
                </Button>
              </>
            )}

            <Divider type="vertical" />
            <Popconfirm
              title={`¿Estás seguro de eliminar ${activeRequirement?.name}?`}
              description={
                <>
                  <p>Los archivos y formularios serán eliminados.</p>
                  <p>
                    Esta acción <b>NO</b> es reversible.{" "}
                  </p>
                </>
              }
              onConfirm={handleRemoveRequirement}
            >
              <Button
                type="primary"
                icon={<DeleteOutlined />}
                loading={deleteRequirementLoading}
                disabled={mutationsLocked}
                danger
              >
                Eliminar requisito
              </Button>
            </Popconfirm>
          </Space>
          {!isValidRequirement && (
            <div className="mt-4">
              <div className="text-left flex flex-col justify-start mt-1 mb-2 font-bold">
                Motivo de rechazo:
              </div>
              <Form.Item<StatusForm> name="rejection_reason" className="mb-3">
                <Select
                  placeholder="Selecciona el motivo de rechazo"
                  options={REJECTION_REASONS}
                  onChange={(value) => setSelectedRejectionReason(value)}
                  className="w-full"
                  optionRender={(option) => (
                    <div
                      className="py-2 px-2 leading-5 whitespace-normal break-words"
                      style={{ wordWrap: "break-word" }}
                    >
                      {option.label}
                    </div>
                  )}
                  labelRender={(option) => (
                    <div className="max-w-[500px] break-word">
                      {option.label}
                    </div>
                  )}
                  getPopupContainer={(trigger) =>
                    trigger.parentElement || document.body
                  }
                />
              </Form.Item>
              {selectedRejectionReason === "other" && (
                <Form.Item<StatusForm> name="invalid_reason">
                  <Input.TextArea
                    rows={3}
                    placeholder="Especifica el motivo de rechazo"
                  />
                </Form.Item>
              )}
            </div>
          )}
        </Form>

        <Divider />

        <div>
          <Typography variant="h6" className="text-left">
            Descripción
          </Typography>
          <div className="text-left flex flex-col justify-start mt-1">
            {description}
          </div>
        </div>

        <Divider />

        <Form
          className="w-full flex flex-col"
          onFinish={onUploadFileForm}
          form={fileForm}
        >
          <div className="w-full">
            <Typography variant="h6">Requisito</Typography>
            <FormView folder={activeFolder!} disabled={mutationsLocked} />

            <span className="text-xs">
              <b>
                Requisito actualizado el{" "}
                {parseDateToSpanishString(updatedAt)}{" "}
              </b>
            </span>
          </div>
          {activeRequirement ? (
            <div>
              <Divider />
              <Typography variant="h6">Archivos</Typography>
              {collectionAtCapacity ? (
                <Typography variant="paragraph">
                  Ya se alcanzó el máximo de {maximumFiles} archivos para este
                  requisito.
                </Typography>
              ) : (
                <Form.Item
                  name="file"
                  getValueFromEvent={(e) =>
                    Array.isArray(e) ? e : e && e.fileList
                  }
                  rules={[
                    {
                      validator: (_, _value) =>
                        !hasValidFileCount
                          ? Promise.reject(
                              minimumNewFiles === maximumNewFiles
                                ? `Selecciona ${minimumNewFiles} archivo${minimumNewFiles === 1 ? "" : "s"}.`
                                : `Selecciona entre ${minimumNewFiles} y ${maximumNewFiles} archivos.`,
                            )
                          : Promise.resolve(),
                    },
                  ]}
                  validateTrigger="onSubmit"
                >
                  <Space wrap className={`flex mt-2`}>
                    <Upload
                      listType="picture"
                      maxCount={maximumNewFiles}
                      multiple={maximumNewFiles > 1}
                      disabled={mutationsLocked}
                      onChange={onChangeFileList}
                      accept={".pdf,.jpg,.jpeg,.png,.webp"}
                      fileList={fileList}
                      beforeUpload={() => false}
                    >
                      <Button icon={<FilePdfOutlined />}>
                        Seleccionar archivo
                      </Button>
                    </Upload>
                    <div>
                      {
                        hasValidFileCount ? (
                          <Button
                            htmlType="submit"
                            icon={<UploadOutlined />}
                            className="left-0"
                            type="primary"
                            loading={loading || uploadIdFilesMutation.isPending}
                            disabled={mutationsLocked}
                          >
                            Subir
                          </Button>
                        ) : (
                          <></>
                        ) /*TODO: descargar todos por requisito <Button icon={<UploadOutlined/>} disabled>Descargar todos</Button>*/
                      }
                    </div>
                  </Space>
                </Form.Item>
              )}
            </div>
          ) : null}
        </Form>
      </div>
      <div>
        {files.length > 0 && (
          <Files
            fileList={files}
            disableDelete={mutationsLocked}
          />
        )}
      </div>

      {verificationData && (
        <div className="mt-4">
          <Divider />
          <Typography variant="h6" className="text-left">
            Datos de Verificación
          </Typography>
          <div className="text-left flex flex-col justify-start mt-3 space-y-2">
            {Object.entries(verificationData).map(([key, value]) => {
              const formatValue = (val: any): any => {
                if (val === null || val === undefined) return "N/A";
                if (typeof val === "string") return val;
                if (typeof val === "boolean") return val ? "Sí" : "No";
                if (Array.isArray(val)) return val.join(", ");
                if (typeof val === "object" && val !== null) {
                  return (
                    <div className="ml-4 space-y-1 mt-1">
                      {Object.entries(val).map(([nestedKey, nestedValue]) => (
                        <div key={nestedKey} className="text-sm">
                          <span className="font-medium">
                            {nestedKey
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                            :
                          </span>{" "}
                          <span>{formatValue(nestedValue)}</span>
                        </div>
                      ))}
                    </div>
                  );
                }
                return String(val);
              };

              return (
                <div
                  key={key}
                  className="bg-gray-50 p-3 rounded-md border border-gray-200"
                >
                  <div className="font-semibold text-gray-700 mb-1">
                    {key
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </div>
                  <div className="text-gray-900">
                    {typeof value === "object" &&
                    value !== null &&
                    !Array.isArray(value) &&
                    !(value instanceof Date) ? (
                      <div className="ml-4 space-y-1">
                        {Object.entries(value).map(([subKey, subValue]) => (
                          <div key={subKey} className="text-sm">
                            <span className="font-medium">
                              {subKey
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (l) => l.toUpperCase())}
                              :
                            </span>{" "}
                            {typeof subValue === "object" &&
                            subValue !== null &&
                            !Array.isArray(subValue) ? (
                              formatValue(subValue)
                            ) : (
                              <span>{formatValue(subValue)}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span>{formatValue(value)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <Modal
        title="Seleccionar archivos para verificar"
        open={isModalOpen}
        onCancel={handleCloseModal}
        onOk={handleVerifyRequirement}
        okText={verifyLoading ? "Verificando..." : "Verificar"}
        cancelText="Cancelar"
        confirmLoading={verifyLoading}
        okButtonProps={{
          disabled: currentFiles.length === 0 || mutationsLocked,
        }}
      >
        <div className="space-y-2">
          <p className="text-gray-600 mb-4">
            Se enviará toda la evidencia vigente para conservar completo el
            lote:
          </p>
          {currentFiles.map((file) => (
            <div
              key={file.id}
              className="flex items-center p-2 hover:bg-gray-50 rounded"
            >
              <Checkbox checked disabled>
                <span className="ml-2">{file.filename}</span>
              </Checkbox>
            </div>
          ))}
          {currentFiles.length === 0 && (
            <p className="text-gray-400 italic">No hay archivos disponibles</p>
          )}
        </div>
      </Modal>
    </Card>
  );
};
